import type { CapturedNetworkRequest } from "posthog-js";

const SENSITIVE_KEY_PATTERN =
  /(^|_)(email|name|prompt|transcript|content|markdown|body|text|token|secret|password|authorization|api_key|phone)($|_)/i;

type AnalyticsScalar = string | number | boolean | null | undefined;
export type SanitizedAnalyticsProperties = Record<string, AnalyticsScalar | AnalyticsScalar[]>;

function isAllowedValue(value: unknown): value is AnalyticsScalar | AnalyticsScalar[] {
  if (value == null) return true;
  if (["string", "number", "boolean"].includes(typeof value)) return true;
  if (!Array.isArray(value)) return false;
  return value.every(
    (item) => item == null || ["string", "number", "boolean"].includes(typeof item)
  );
}

export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>
): SanitizedAnalyticsProperties {
  const sanitized: SanitizedAnalyticsProperties = {};

  for (const [key, value] of Object.entries(properties)) {
    if (SENSITIVE_KEY_PATTERN.test(key) || !isAllowedValue(value)) continue;
    if (value === undefined) continue;
    sanitized[key] = value;
  }

  return sanitized;
}

export function redactUrlQueryString(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString();
  } catch {
    const [withoutHash] = url.split("#");
    return withoutHash?.split("?")[0] ?? url;
  }
}

export function redactSensitiveUrlAndRequestData(
  request: CapturedNetworkRequest
): CapturedNetworkRequest | null {
  return {
    ...request,
    name: redactUrlQueryString(request.name) ?? request.name,
    requestBody: undefined,
    requestHeaders: undefined,
    responseBody: undefined,
    responseHeaders: undefined,
  };
}
